import {
  Box,
  Column,
  Columns,
  Divider,
  Radio,
  RadioGroup,
  Stack,
  Text,
} from "@origin-digital/ods-core";
import * as React from "react";
import { Contact, FormValues } from "../types";
import { useFormikContext, yupToFormErrors } from "formik";
import { format } from "date-fns";
import { apiClient } from "src/api/apiService";
import { usePermissions } from "src/hooks/usePermissions";
import { useResponsiveBelowMd, useResponsiveBelowSm } from "src/hooks";
import FormFieldLayout from "../FormComponents/FormFieldLayout";
import { DatePickerWrapper } from "../FormComponents/DatePickerWrapper";
import { getNestedValue, isDateWithinNext42Days } from "../helper";
import PermissionsActionButtons from "../../Form/PermissionsActionButtons";
import { validateHandoverDate } from "../FormComponents/Validations";
import { InternetRequestRawStatusEnum } from "../ServiceTypeStatusAlert";
import { RequestStatusEnum } from "../../Premises/types";
import { FormContext } from "src/context/NewPremiseFormContext/context";
import { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "src/context/AppContext";

type HOMEUSER_SS_PI = {
  first_name: string;
  last_name: string;
  phone_number: string;
  email: string;
};
type Contact_Type = Contact | HOMEUSER_SS_PI;

const ReadOnlyPreinstallNBNBlock = () => {
  const [siteSupervisor, setSiteSupervisor] = React.useState<Contact_Type>();
  const [preInstaller, setPreInstaller] = React.useState<Contact_Type>();
  const [preinstallationComplete, setPreinstallationComplete] =
    React.useState<string>("no");
  const [handoverDateChanged, setHandoverDateChanged] =
    React.useState<boolean>(false);
  const [preinstallDone, setPreinstallDone] = React.useState<string | null>(
    null
  );
  const [initHandoverDate, setInitHandoverDate] = React.useState<string | null>(
    null
  );
  const { values, errors, setFieldValue, setErrors } =
    useFormikContext<FormValues>();
  const { isHomeUser } = usePermissions();
  const isBelowMd = useResponsiveBelowMd();
  const isBelowSm = useResponsiveBelowSm();

  const navigate = useNavigate();
  const { setDisplayingAlertBanner, setDisplayingSnackbar } =
    useContext(FormContext);
  const {
    context: { user },
  } = useAppContext();
  const isOriginAdmin = user?.role === "ORIGIN_ADMIN";
  const fetchSiteSupervisor = async () => {
    const msaNumber = values.requestingCompany?.msaNumber;
    if (values.internetSiteSupervisor && msaNumber) {
      try {
        const response = await apiClient.get<Contact[]>(
          `/contact/${msaNumber}`,
          {
            params: { type: "SUPERVISOR" },
          }
        );
        const matchingSupervisor = response.data.find(
          (contact) => contact.id === values.internetSiteSupervisor
        );
        if (matchingSupervisor) {
          setSiteSupervisor(matchingSupervisor);
        }
      } catch (error) {
        console.log(`Fetch site supervisor error: ${error}`);
      }
    }
  };
  const fetchPreInstaller = async () => {
    const msaNumber = values.requestingCompany?.msaNumber;
    if (values.internetPreInstaller && msaNumber) {
      try {
        const response = await apiClient.get<Contact[]>(
          `/contact/${msaNumber}`,
          {
            params: { type: "INTERNET_PREINSTALLER" },
          }
        );
        const matchingPreInstaller = response.data.find(
          (contact) => contact.id === values.internetPreInstaller
        );
        if (matchingPreInstaller) {
          setPreInstaller(matchingPreInstaller);
        }
      } catch (error) {
        console.log(`Fetch pre-installer error: ${error}`);
      }
    }
  };
  React.useEffect(() => {
    if (values.internetHandoverDate) {
      const initialDate = new Date(values.internetHandoverDate).toISOString();
      setInitHandoverDate(initialDate);
    }
  }, []);
  React.useEffect(() => {
    if (
      isHomeUser &&
      typeof values.internetSiteSupervisor === "object" &&
      values.internetSiteSupervisor !== null
    ) {
      setSiteSupervisor({
        first_name: values.internetSiteSupervisor.first_name ?? "",
        last_name: values.internetSiteSupervisor.last_name ?? "",
        phone_number: values.internetSiteSupervisor.phone_number ?? "",
        email: values.internetSiteSupervisor.email ?? "",
      });
    } else fetchSiteSupervisor();
  }, [values.internetSiteSupervisor, values.requestingCompany?.msaNumber]);

  React.useEffect(() => {
    if (
      isHomeUser &&
      typeof values.internetPreInstaller === "object" &&
      values.internetPreInstaller !== null
    ) {
      setPreInstaller({
        first_name: values.internetPreInstaller.first_name ?? "",
        last_name: values.internetPreInstaller.last_name ?? "",
        phone_number: values.internetPreInstaller.phone_number ?? "",
        email: values.internetPreInstaller.email ?? "",
      });
    } else fetchPreInstaller();
  }, [values.internetPreInstaller, values.requestingCompany?.msaNumber]);

  const internetSiteSupervisorDetails =
    typeof values.internetSiteSupervisor === "object" &&
    values.internetSiteSupervisor !== null
      ? values.internetSiteSupervisor
      : undefined;

  const internetPreInstallerDetails =
    typeof values.internetPreInstaller === "object" &&
    values.internetPreInstaller !== null
      ? values.internetPreInstaller
      : undefined;

  const shouldAskPreinstallationQuestions = isOriginAdmin
    ? values.internetRequestRawStatus ===
        InternetRequestRawStatusEnum.REPLY_NEEDED ||
      values.internetRequestRawStatus === InternetRequestRawStatusEnum.SCHEDULED
    : values.internetRequestRawStatus ===
      InternetRequestRawStatusEnum.REPLY_NEEDED;
  const cantBeCompleted = Boolean(
    values.internetRequestRawStatus === InternetRequestRawStatusEnum.SCHEDULED
  );

  const handoverDate = values.internetHandoverDate
    ? format(new Date(values.internetHandoverDate), "dd/MM/yyyy")
    : "";
  const preInstallationDate = values.internetPreinstallationDate
    ? format(new Date(values.internetPreinstallationDate), "dd/MM/yyyy")
    : "";

  const value: string | null =
    getNestedValue(values, "internetHandoverDate") ?? null;
  const errorInDt: string | undefined = getNestedValue(
    errors,
    "internetHandoverDate"
  );
  const helperText = errorInDt?.toString();
  const handleOnChange = React.useCallback(
    (date: Date | null) => {
      if (date != value) {
        setFieldValue("internetHandoverDate", date);
      }
      if (date && date.toISOString() !== initHandoverDate) {
        setHandoverDateChanged(true);
      } else if (date && date.toISOString() === initHandoverDate) {
        setHandoverDateChanged(false);
      }
    },
    [setFieldValue, value, initHandoverDate]
  );

  const presubmissionRequestUpdate = async () => {
    try {
      const payload: {
        request_id?: string | number;
        handover_date?: Date | string | null;
        preinstallation_complete?: boolean | null;
        preinstallation_cancelled_by_bspec?: boolean | null;
      } = {};
      payload.request_id = values.internetRequestId;
      payload.handover_date = values.internetHandoverDate;
      if (preinstallationComplete === "yes")
        payload.preinstallation_complete = true;
      if (preinstallationComplete === "cancelled")
        payload.preinstallation_cancelled_by_bspec = true;

      await apiClient.put(`/internet/status-update`, payload);

      setDisplayingAlertBanner(null);
      navigate("/", {
        state: {
          submitted: true,
        },
      });
    } catch (e: any) {
      // setDisplayingAlertBanner(getErrorTypeEnum(e) ?? null);
      setDisplayingSnackbar({
        message: "Failed to update Internet request.",
        tone: "critical",
      });
    }
  };
  const showReadonlyHandoverDate = Boolean(
    values.internetEicStatus === "EIC_COMM_SENT" ||
      values.internetEicStatus === "EIC_COMM_REMINDER_SENT" ||
      values.internetComputedRequestStatus === RequestStatusEnum.CANCELLED
  );

  const showEditableHandoverDateAndButton = Boolean(
    values.internetEicStatus === "EIC_COMM_NOT_SENT" &&
      values.internetComputedRequestStatus !== RequestStatusEnum.CANCELLED
  );

  return (
    <Stack
      space={{
        xs: "xlarge",
        md: "xxlarge",
      }}
    >
      {/* Form container */}
      <Stack
        space={{
          xs: "xlarge",
          md: "xxlarge",
        }}
      >
        {/* ID's and dates */}
        <Columns
          collapseBelow="md"
          space={{
            xs: "none",
            md: "xlarge",
          }}
          alignY="stretch"
        >
          {/* Fields */}
          <Column width="2/3">
            <Stack
              space={{
                xs: "large",
                md: "xlarge",
              }}
            >
              {/* Row */}
              <Columns
                collapseBelow="md"
                space={{
                  xs: "xlarge",
                  md: "medium",
                }}
              >
                <Column>
                  {/* LOC ID */}
                  <Stack space="xsmall">
                    <Stack space="xxsmall">
                      <Text variant="body-small" tone="neutralLight">
                        LOC ID
                      </Text>
                      <Text>{values.locId}</Text>
                    </Stack>
                    <Divider />
                  </Stack>
                  {/* End LOC ID */}
                </Column>
                {values.internetJobId ? (
                  <Column>
                    {/* Job ID */}
                    <Stack space="xsmall">
                      <Stack space="xxsmall">
                        <Text variant="body-small" tone="neutralLight">
                          Job ID
                        </Text>
                        <Text>{values.internetJobId}</Text>
                      </Stack>
                      <Divider />
                    </Stack>
                    {/* End Job ID */}
                  </Column>
                ) : isBelowMd ? null : (
                  <Column />
                )}
              </Columns>
              {/* End row */}
              {/* Row */}
              <Columns
                collapseBelow="md"
                space={{
                  xs: "xlarge",
                  md: "medium",
                }}
              >
                <Column>
                  {/* Pre-installation date */}
                  <Stack space="xsmall">
                    <Stack space="xxsmall">
                      <Text variant="body-small" tone="neutralLight">
                        Pre-installation date
                      </Text>
                      <Text>{preInstallationDate}</Text>
                    </Stack>
                    <Divider />
                  </Stack>
                  {/* End Pre-installation date */}
                </Column>
                {showReadonlyHandoverDate ? (
                  <Column>
                    <Stack space="xsmall">
                      <Stack space="xxsmall">
                        <Text variant="body-small" tone="neutralLight">
                          Handover date
                        </Text>
                        <Text>{handoverDate}</Text>
                      </Stack>
                      <Divider />
                    </Stack>
                  </Column>
                ) : isBelowMd ? null : (
                  <Column />
                )}
              </Columns>
              {/* End row */}
            </Stack>
          </Column>
          {/* Fields */}
          {/* HelperText */}
          <Column width="1/3" />
          {/* End HelperText */}
        </Columns>
        {/* End ID's and dates */}
        {/* Internet connection address */}
        {values.internetNbnAddress && (
          <Columns
            collapseBelow="md"
            space={{
              xs: "none",
              md: "xlarge",
            }}
            alignY="stretch"
          >
            <Column width="2/3">
              <Stack space="xsmall">
                <Stack space="xxsmall">
                  <Text variant="body-small" tone="neutralLight">
                    Internet connection address
                  </Text>
                  <Text>{values.internetNbnAddress}</Text>
                </Stack>
                <Divider />
              </Stack>
            </Column>
            <Column width="1/3" />
          </Columns>
        )}
        {/* End Internet connection address */}
        {showEditableHandoverDateAndButton ? (
          <Columns
            collapseBelow="md"
            space={{
              xs: "none",
              md: "xlarge",
            }}
            alignY="stretch"
          >
            {/* Fields */}
            <Column width="2/3">
              <Columns collapseBelow="sm">
                <Column width="3/4">
                  <FormFieldLayout>
                    <DatePickerWrapper
                      id="internetHandoverDate"
                      label="Handover date"
                      value={
                        values?.internetHandoverDate
                          ? new Date(values.internetHandoverDate)
                          : value
                      }
                      onChange={handleOnChange}
                      shouldDisableDate={isDateWithinNext42Days}
                      helperText={helperText}
                      error={!!helperText}
                    />
                  </FormFieldLayout>
                </Column>
                {isBelowSm ? null : <Column />}
              </Columns>
            </Column>
            {/* Fields */}
            {/* HelperText */}
            <Column width="1/3">
              <Box display="flex" alignItems="stretch" height="full">
                {!isBelowMd && (
                  <Box
                    style={{ borderLeft: "1px solid #E3E3E3" }}
                    paddingRight="large"
                    height="full"
                  />
                )}
                <Box alignSelf="center" marginTop="xsmall">
                  <Stack>
                    <Text variant="body-small" tone="neutralLight">
                      Handover date must be at least 4 weeks after the
                      pre-installation date.
                    </Text>
                    <Text variant="body-small" tone="neutralLight">
                      Once the request is lodged, you can change the handover
                      date if it’s still more than 30 days away. Once it’s less
                      than 30 days away, the date is locked and can’t be
                      changed.
                    </Text>
                  </Stack>
                </Box>
              </Box>
            </Column>
          </Columns>
        ) : null}
        {/* Pre-installer */}
        {(preInstaller?.email || internetPreInstallerDetails?.email) && (
          <Columns
            collapseBelow="md"
            space={{
              xs: "none",
              md: "xlarge",
            }}
            alignY="stretch"
          >
            {/* Fields */}
            <Column width="2/3">
              <Stack
                space={{
                  xs: "large",
                  md: "xlarge",
                }}
              >
                <Text weight="medium">Pre-installer</Text>
                {/* First + last name */}
                <Columns
                  collapseBelow="md"
                  space={{
                    xs: "xlarge",
                    md: "medium",
                  }}
                >
                  <Column>
                    {/* First name */}
                    <Stack space="xsmall">
                      <Stack space="xxsmall">
                        <Text variant="body-small" tone="neutralLight">
                          First name
                        </Text>
                        <Text>
                          {preInstaller?.first_name ||
                            internetPreInstallerDetails?.first_name}
                        </Text>
                      </Stack>
                      <Divider />
                    </Stack>
                    {/* End First name */}
                  </Column>
                  <Column>
                    {/* Last name */}
                    <Stack space="xsmall">
                      <Stack space="xxsmall">
                        <Text variant="body-small" tone="neutralLight">
                          Last name
                        </Text>
                        <Text>
                          {preInstaller?.last_name ||
                            internetPreInstallerDetails?.last_name}
                        </Text>
                      </Stack>
                      <Divider />
                    </Stack>
                    {/* End Last name */}
                  </Column>
                </Columns>
                {/* End First + last name */}
                {/* Phone */}
                <Columns
                  collapseBelow="md"
                  space={{
                    xs: "xlarge",
                    md: "medium",
                  }}
                >
                  <Column>
                    <Stack space="xsmall">
                      <Stack space="xxsmall">
                        <Text variant="body-small" tone="neutralLight">
                          Phone
                        </Text>
                        <Text>
                          {preInstaller?.phone_number ||
                            internetPreInstallerDetails?.phone_number}
                        </Text>
                      </Stack>
                      <Divider />
                    </Stack>
                  </Column>
                  {isBelowMd ? null : <Column />}
                </Columns>
                {/* End Phone */}
                {/* Email */}
                <Stack space="xsmall">
                  <Stack space="xxsmall">
                    <Text variant="body-small" tone="neutralLight">
                      Email
                    </Text>
                    <Text>
                      {preInstaller?.email ||
                        internetPreInstallerDetails?.email}
                    </Text>
                  </Stack>
                  <Divider />
                </Stack>
                {/* End Email */}
              </Stack>
            </Column>
            {/* End Fields */}
            {/* HelperText */}
            <Column width="1/3" />
            {/* End HelperText */}
          </Columns>
        )}
        {/* End Pre-installer */}

        {/* Site supervisor */}
        <Columns
          collapseBelow="md"
          space={{
            xs: "none",
            md: "xlarge",
          }}
          alignY="stretch"
        >
          {/* Fields */}
          <Column width="2/3">
            <Stack
              space={{
                xs: "large",
                md: "xlarge",
              }}
            >
              <Text weight="medium">Site supervisor</Text>
              {/* First + last name */}
              <Columns
                collapseBelow="md"
                space={{
                  xs: "xlarge",
                  md: "medium",
                }}
              >
                <Column>
                  {/* First name */}
                  <Stack space="xsmall">
                    <Stack space="xxsmall">
                      <Text variant="body-small" tone="neutralLight">
                        First name
                      </Text>
                      <Text>
                        {siteSupervisor?.first_name ||
                          internetSiteSupervisorDetails?.first_name}
                      </Text>
                    </Stack>
                    <Divider />
                  </Stack>
                  {/* End First name */}
                </Column>
                <Column>
                  {/* Last name */}
                  <Stack space="xsmall">
                    <Stack space="xxsmall">
                      <Text variant="body-small" tone="neutralLight">
                        Last name
                      </Text>
                      <Text>
                        {siteSupervisor?.last_name ||
                          internetSiteSupervisorDetails?.last_name}
                      </Text>
                    </Stack>
                    <Divider />
                  </Stack>
                  {/* End Last name */}
                </Column>
              </Columns>
              {/* End First + last name */}
              {/* Phone */}
              <Columns
                collapseBelow="md"
                space={{
                  xs: "xlarge",
                  md: "medium",
                }}
              >
                <Column>
                  <Stack space="xsmall">
                    <Stack space="xxsmall">
                      <Text variant="body-small" tone="neutralLight">
                        Phone
                      </Text>
                      <Text>
                        {siteSupervisor?.phone_number ||
                          internetSiteSupervisorDetails?.phone_number}
                      </Text>
                    </Stack>
                    <Divider />
                  </Stack>
                </Column>
                {isBelowMd ? null : <Column />}
              </Columns>
              {/* End Phone */}
              {/* Email */}
              <Stack space="xsmall">
                <Stack space="xxsmall">
                  <Text variant="body-small" tone="neutralLight">
                    Email
                  </Text>
                  <Text>
                    {siteSupervisor?.email ||
                      internetSiteSupervisorDetails?.email}
                  </Text>
                </Stack>
                <Divider />
              </Stack>
              {/* End Email */}
            </Stack>
          </Column>
          {/* End Fields */}
          {/* HelperText */}
          <Column width="1/3" />
          {/* End HelperText */}
        </Columns>
        {/* End Site supervisor */}

        {/* Pre-install completed */}
        <Columns
          collapseBelow="md"
          space={{
            xs: "none",
            md: "xlarge",
          }}
          alignY="stretch"
        >
          {shouldAskPreinstallationQuestions && isOriginAdmin ? (
            <>
              <Column width="2/3">
                <RadioGroup
                  name="preinstallationComplete"
                  label="Has the status of this request changed?"
                  value={preinstallationComplete}
                  onChange={(e) => {
                    setPreinstallationComplete(e.currentTarget.value);
                    if (e.currentTarget.value === "yes") {
                      setPreinstallDone("yes");
                    } else if (e.currentTarget.value === "no") {
                      setPreinstallDone("no");
                    } else if (e.currentTarget.value === "cancelled") {
                      setPreinstallDone("cancelled");
                    }
                  }}
                >
                  <Radio id="no" label="No change" value="no" />
                  <Radio
                    id="yes"
                    label="Pre-installation has been completed"
                    value="yes"
                    disabled={cantBeCompleted}
                  />
                  <Radio
                    id="cancelled"
                    label="Pre-installation has been cancelled by NBN"
                    value="cancelled"
                  />
                </RadioGroup>
              </Column>
              <Column width="1/3">
                <Box display="flex" alignItems="stretch" height="full">
                  {!isBelowMd && (
                    <Box
                      style={{ borderLeft: "1px solid #E3E3E3" }}
                      paddingRight="large"
                      height="full"
                    />
                  )}
                  <Box alignSelf="center" marginTop="xsmall">
                    <Text variant="body-small" tone="neutralLight">
                      You can select the option as either 'Pre-installation has
                      been completed'
                      {isOriginAdmin &&
                        " or 'Pre-installation cancelled by NBN'"}{" "}
                      only once, so ensure the correct the option is selected as
                      the status in OCP will change accordingly.
                    </Text>
                  </Box>
                </Box>
              </Column>
            </>
          ) : shouldAskPreinstallationQuestions && !isOriginAdmin ? (
            <>
              <Column width="2/3">
                <RadioGroup
                  name="preinstallationComplete"
                  label="Has the status of this request changed?"
                  value={preinstallationComplete}
                  onChange={(e) => {
                    setPreinstallationComplete(e.currentTarget.value);
                    if (e.currentTarget.value === "yes") {
                      setPreinstallDone("yes");
                    } else {
                      setPreinstallDone("no");
                    }
                  }}
                >
                  <Radio id="no" label="No change" value="no" />
                  <Radio
                    id="yes"
                    label="Pre-installation has been completed"
                    value="yes"
                  />
                </RadioGroup>
              </Column>
              <Column width="1/3">
                <Box display="flex" alignItems="stretch" height="full">
                  {!isBelowMd && (
                    <Box
                      style={{ borderLeft: "1px solid #E3E3E3" }}
                      paddingRight="large"
                      height="full"
                    />
                  )}
                  <Box alignSelf="center" marginTop="xsmall">
                    <Text variant="body-small" tone="neutralLight">
                      You can select the option as either 'Pre-installation has
                      been completed'
                      {isOriginAdmin &&
                        "or 'Pre-installation cancelled by NBN'"}{" "}
                      only once, so ensure the correct the option is selected as
                      the status in OCP will change accordingly.
                    </Text>
                  </Box>
                </Box>
              </Column>
            </>
          ) : null}
        </Columns>
        {/* End Pre-install completed */}
      </Stack>

      {/* End Timestamps */}
      {showEditableHandoverDateAndButton && (
        <PermissionsActionButtons
          primaryButtonProps={{
            onHandleClick: async () => {
              // Validate handover date before submission
              const validationResult = validateHandoverDate(
                values.internetHandoverDate ?? null
              );

              if (!validationResult.isValid) {
                // Set error for handover date field
                setErrors({
                  ...errors,
                  internetHandoverDate: validationResult.errorMessage,
                });
                return;
              }
              try {
                await presubmissionRequestUpdate();
              } catch (e) {
                console.log("e", e);
                setErrors(yupToFormErrors(e));
              }
            },
            disabled:
              handoverDateChanged !== true &&
              preinstallDone !== "yes" &&
              preinstallDone !== "cancelled", // Disable if handover date hasn't changed and pre-installation isn't marked as done
          }}
          isInternet={true}
          showSecondaryButton={false}
        />
      )}
    </Stack>
  );
};
export default ReadOnlyPreinstallNBNBlock;
